import { PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import {
    Area,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import api from "../../services/api";
import { styles } from "../../styles";
import { safeNumber } from "../../utils/helpers";
import TimeHeatmap from './TimeHeatmap';
import EdgeReport from '../ai/EdgeReport';

/**
 * Mirror of IntelligentAnalyticsService.calculateDateRange on the backend.
 * Converts a period string into { dateFrom, dateTo } ISO strings for APIs
 * that only accept absolute date params (mae-mfe, comprehensive, time-heatmap).
 */
function periodToDateRange(period, appliedCustomDates) {
  const now = new Date();
  switch (period) {
    case 'week': {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      return { dateFrom: d.toISOString(), dateTo: now.toISOString() };
    }
    case 'month': {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { dateFrom: d.toISOString(), dateTo: now.toISOString() };
    }
    case 'year':
    case 'ytd': {
      const d = new Date(now.getFullYear(), 0, 1);
      return { dateFrom: d.toISOString(), dateTo: now.toISOString() };
    }
    case 'all':
      return { dateFrom: null, dateTo: null };
    case 'custom':
      return {
        dateFrom: appliedCustomDates.start ? new Date(appliedCustomDates.start).toISOString() : null,
        dateTo:   appliedCustomDates.end   ? new Date(appliedCustomDates.end).toISOString()   : now.toISOString(),
      };
    default: {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { dateFrom: d.toISOString(), dateTo: now.toISOString() };
    }
  }
}

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [customDates, setCustomDates] = useState({ start: "", end: "" });
  const [appliedCustomDates, setAppliedCustomDates] = useState({ start: "", end: "" });
  const [showCustomDates, setShowCustomDates] = useState(false);
  // Phase 4: MAE/MFE real data + comprehensive metrics
  const [maeMfeData, setMaeMfeData] = useState([]);
  const [comprehensiveMetrics, setComprehensiveMetrics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchMaeMfe();
    fetchComprehensiveMetrics();
  }, [period, appliedCustomDates]);

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom" && appliedCustomDates.start && appliedCustomDates.end) {
        params.append("startDate", appliedCustomDates.start);
        params.append("endDate", appliedCustomDates.end);
      }

      const { data } = await api.get(`/analytics/intelligent?${params}`);
      if (data.success) {
        setAnalytics(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  // Phase 4: Fetch real MAE/MFE data
  const fetchMaeMfe = async () => {
    try {
      const { dateFrom, dateTo } = periodToDateRange(period, appliedCustomDates);
      const params = new URLSearchParams();
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo)   params.append("dateTo",   dateTo);
      const { data } = await api.get(`/analytics/mae-mfe?${params}`);
      if (data.success) setMaeMfeData(data.data || []);
    } catch {
      // non-fatal
    }
  };

  // Phase 4: Fetch comprehensive metrics (Sortino, Calmar, Expectancy, etc.)
  const fetchComprehensiveMetrics = async () => {
    try {
      const { dateFrom, dateTo } = periodToDateRange(period, appliedCustomDates);
      const params = new URLSearchParams();
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo)   params.append("dateTo",   dateTo);
      const { data } = await api.get(`/analytics/comprehensive?${params}`);
      if (data.success) setComprehensiveMetrics(data.data);
    } catch {
      // non-fatal
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== "custom") {
      setCustomDates({ start: "", end: "" });
      setShowCustomDates(false);
    } else {
      setShowCustomDates(true);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        Loading intelligent analytics...
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        No analytics data available. Complete some trades to see insights.
      </div>
    );
  }

  const {
    strategyAnalysis,
    setupAnalysis,
    timePatterns,
    durationAnalysis,
    winLossComparison,
    riskRewardAnalysis,
    mistakeAnalysis,
    maeMfeAnalysis,
    rMultipleAnalysis,
    drawdownAnalysis,
    recommendations,
  } = analytics;

  // Prepare chart data for time patterns
  const hourlyChartData = timePatterns.hourlyPatterns
    .filter(h => h.count > 0)
    .map(h => ({
      hour: `${h.hour}:00`,
      avgPnl: h.avgPnl,
      count: h.count,
      winRate: h.winRate
    }));

  const dailyChartData = timePatterns.dailyPatterns
    .filter(d => d.count > 0)
    .map(d => ({
      day: d.day.substring(0, 3),
      avgPnl: d.avgPnl,
      count: d.count,
      winRate: d.winRate
    }));

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: "bold", color: "var(--text-primary)", margin: 0 }}>
          Intelligent Analytics
        </h1>
        <p style={{ color: "var(--text-muted)", marginTop: "0.5rem" }}>
          Data-driven insights to improve your trading performance
        </p>
      </div>

      {/* Period Selector */}
      <div style={{ ...styles.card, padding: "1rem", marginBottom: "2rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "var(--text-primary)", marginRight: "0.5rem" }}>
            Period:
          </span>
          {["week", "month", "year", "ytd", "all", "custom"].map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              style={{
                ...styles.button,
                ...(period === p ? {} : styles.buttonSecondary),
                fontSize: "0.875rem",
                padding: "0.5rem 1rem",
              }}
            >
              {p === "ytd" ? "YTD" : p === "all" ? "All Time" : p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {showCustomDates && (
          <div style={{ display: "flex", gap: "1rem", marginTop: "1rem", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: "0.25rem" }}>
                Start Date
              </label>
              <input
                type="date"
                value={customDates.start}
                onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
                style={styles.input}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: "0.25rem" }}>
                End Date
              </label>
              <input
                type="date"
                value={customDates.end}
                onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
                style={styles.input}
              />
            </div>
            <button
              onClick={() => setAppliedCustomDates({ ...customDates })}
              disabled={!customDates.start || !customDates.end}
              style={{
                padding: "0.5rem 1.25rem",
                borderRadius: "0.5rem",
                background: customDates.start && customDates.end ? "#10b981" : "#334155",
                color: customDates.start && customDates.end ? "#fff" : "#64748b",
                border: "none",
                cursor: customDates.start && customDates.end ? "pointer" : "not-allowed",
                fontWeight: 600,
                fontSize: "0.875rem",
                alignSelf: "flex-end",
                marginBottom: "2px",
              }}
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Smart Video Recommendations */}
      {recommendations && recommendations.recommendations && recommendations.recommendations.length > 0 && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem", color: "var(--text-primary)" }}>
            🎯 Smart Recommendations
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem" }}>
            {recommendations.recommendations.map((rec, idx) => (
              <div
                key={idx}
                style={{
                  padding: "1rem",
                  borderLeft: `5px solid ${
                    rec.priority === "critical" ? "var(--danger)" :
                    rec.priority === "high" ? "var(--warning)" : "var(--primary)"
                  }`,
                  backgroundColor: "var(--bg-tertiary)",
                  borderRadius: "0.5rem",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "0.5rem" }}>
                  <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--text-primary)", margin: 0 }}>
                    {rec.title}
                  </h3>
                  {rec.type === 'video' && <PlayCircle size={20} color="var(--danger)" />}
                </div>
                
                <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
                  {rec.message}
                </p>

                {rec.type === 'video' && rec.video && (
                  <a 
                    href={rec.video.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      textDecoration: "none"
                    }}
                  >
                    <div style={{ position: "relative", borderRadius: "0.5rem", overflow: "hidden" }}>
                      <img 
                        src={rec.video.thumbnail} 
                        alt={rec.video.title} 
                        style={{ width: "100%", height: "auto", display: "block" }} 
                      />
                      <div style={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center"
                      }}>
                        <PlayCircle size={48} color="var(--bg-secondary)" fill="rgba(0,0,0,0.5)" />
                      </div>
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      Watch: {rec.video.title}
                    </div>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 4: Comprehensive Metrics Card */}
      {comprehensiveMetrics && (
        <div className="bg-gray-800/50 rounded-xl border border-white/10 p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-4">Advanced Performance Metrics</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { label: 'Sortino Ratio', key: 'sortino', fmt: v => v != null ? v.toFixed(3) : '—', tip: 'Like Sharpe, but only penalizes downside volatility' },
              { label: 'Calmar Ratio', key: 'calmar', fmt: v => v != null ? v.toFixed(3) : '—', tip: 'Annualized return / max drawdown' },
              { label: 'Expectancy', key: 'expectancy', fmt: v => v != null ? `$${v.toFixed(2)}` : '—', tip: 'Expected P&L per trade' },
              { label: 'Recovery Factor', key: 'recovery_factor', fmt: v => v != null ? v.toFixed(2) : '—', tip: 'Total profit / max drawdown' },
              { label: 'Avg R-Multiple', key: 'avg_r_multiple', fmt: v => v != null ? `${v.toFixed(2)}R` : '—', tip: 'Average risk-adjusted return per trade' },
              { label: 'Max Win Streak', key: 'max_consecutive_wins', fmt: v => v != null ? `${v} trades` : '—', tip: 'Longest consecutive winning streak' },
              { label: 'Max Loss Streak', key: 'max_consecutive_losses', fmt: v => v != null ? `${v} trades` : '—', tip: 'Longest consecutive losing streak' },
              { label: 'Avg Hold Time', key: 'avg_hold_time', fmt: v => {
                if (!v || v.all == null) return '—';
                const mins = v.all;
                if (mins < 60) return `${Math.round(mins)}m`;
                if (mins < 1440) return `${(mins/60).toFixed(1)}h`;
                return `${(mins/1440).toFixed(1)}d`;
              }, tip: 'Average trade duration' },
            ].map(({ label, key, fmt, tip }) => (
              <div key={key} className="bg-gray-900/60 rounded-lg p-3" title={tip}>
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className="text-base font-bold text-white">{fmt(comprehensiveMetrics[key])}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MAE/MFE Analysis — wired to real data (Phase 4) */}
      {(maeMfeData.length > 0 || (maeMfeAnalysis && maeMfeAnalysis.hasData)) && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem", color: "var(--text-primary)" }}>
            ⚖️ MAE vs MFE (Trade Efficiency)
          </h2>
          <div style={{ fontSize: "0.9rem", color: "var(--text-muted)", marginBottom: "1.5rem" }}>
            Compare Maximum Adverse Excursion (Stop Loss Proximity) vs Maximum Favorable Excursion (Profit Potential).
            Winners cluster top-left; revenge trades show large MAE with small MFE.
          </div>
          <div style={{ height: "400px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid />
                <XAxis type="number" dataKey="mae" name="MAE" unit="$" label={{ value: 'MAE (Max Pain)', position: 'bottom', offset: 0 }} />
                <YAxis type="number" dataKey="mfe" name="MFE" unit="$" label={{ value: 'MFE (Max Gain)', angle: -90, position: 'insideLeft' }} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n) => [`$${v}`, n]} />
                <Legend />
                <Scatter
                  name="Wins"
                  data={(maeMfeData.length > 0 ? maeMfeData : maeMfeAnalysis?.dataPoints || []).filter(d => d.pnl > 0 || d.isWin)}
                  fill="var(--success)"
                  shape="circle"
                />
                <Scatter
                  name="Losses"
                  data={(maeMfeData.length > 0 ? maeMfeData : maeMfeAnalysis?.dataPoints || []).filter(d => d.pnl <= 0 && !d.isWin)}
                  fill="var(--danger)"
                  shape="cross"
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          {maeMfeData.length === 0 && (
            <p className="text-xs text-gray-500 text-center mt-2">
              Add MAE/MFE values to trades to populate this chart with real data.
            </p>
          )}
        </div>
      )}

      {/* R-Multiple Distribution */}
      {rMultipleAnalysis && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem", color: "var(--text-primary)" }}>
            🎯 R-Multiple Distribution
          </h2>
          {rMultipleAnalysis.totalTradesWithRisk >= 3 ? (
            <>
              <div style={{ marginBottom: "1rem", display: "flex", gap: "2rem", padding: "1rem", backgroundColor: "var(--bg-tertiary)", borderRadius: "0.5rem" }}>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Average R</span>
                  <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>{rMultipleAnalysis.averageR}R</div>
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Expectancy</span>
                  <div style={{ fontSize: "1.5rem", fontWeight: "700", color: rMultipleAnalysis.expectancy > 0 ? "var(--success)" : "var(--danger)" }}>
                    {rMultipleAnalysis.expectancy}R
                  </div>
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Trades with Risk Data</span>
                  <div style={{ fontSize: "1.5rem", fontWeight: "700" }}>{rMultipleAnalysis.totalTradesWithRisk}</div>
                </div>
              </div>
              <div style={{ height: "300px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Object.entries(rMultipleAnalysis.distribution).map(([key, value]) => ({ bucket: key, count: value }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="var(--primary)">
                      {Object.entries(rMultipleAnalysis.distribution).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index < 3 ? '#ef4444' : '#10b981'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📊</div>
              <div style={{ fontWeight: "600", marginBottom: "0.5rem" }}>Insufficient data</div>
              <div style={{ fontSize: "0.875rem" }}>
                R-Multiple requires at least 3 trades with a stop-loss set.
                {rMultipleAnalysis.totalTradesWithRisk > 0 && (
                  <span> ({rMultipleAnalysis.totalTradesWithRisk} trade{rMultipleAnalysis.totalTradesWithRisk === 1 ? '' : 's'} with risk data so far)</span>
                )}
                {' '}Add stop-loss prices to your trades to see R-Multiple distribution.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Account Drawdown */}
      {drawdownAnalysis && drawdownAnalysis.equityCurve && drawdownAnalysis.equityCurve.length > 0 ? (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem", color: "var(--text-primary)" }}>
            📉 Equity & Drawdown
          </h2>
          <div style={{ display: "flex", gap: "2rem", marginBottom: "1rem", padding: "1rem", backgroundColor: "var(--bg-tertiary)", borderRadius: "0.5rem" }}>
            <div>
              <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Peak Equity</span>
              <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--success)" }}>${drawdownAnalysis.peakEquity?.toFixed(2)}</div>
            </div>
            <div>
              <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Max Drawdown</span>
              <div style={{ fontSize: "1.25rem", fontWeight: "700", color: "var(--danger)" }}>-${drawdownAnalysis.maxDrawdown?.toFixed(2)}</div>
            </div>
            <div>
              <span style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Current Drawdown</span>
              <div style={{ fontSize: "1.25rem", fontWeight: "700", color: drawdownAnalysis.currentDrawdown > 0 ? "var(--danger)" : "var(--success)" }}>
                {drawdownAnalysis.currentDrawdown > 0 ? '-' : ''}${drawdownAnalysis.currentDrawdown?.toFixed(2)}
              </div>
            </div>
          </div>
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={drawdownAnalysis.equityCurve}>
                <CartesianGrid stroke="#f5f5f5" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(val) => {
                    try { return new Date(val).toLocaleDateString(); } catch { return val; }
                  }}
                  minTickGap={40}
                />
                <YAxis yAxisId="left" orientation="left" stroke="var(--primary)" />
                <YAxis yAxisId="right" orientation="right" stroke="var(--danger)" />
                <Tooltip
                  labelFormatter={(label) => {
                    try { return new Date(label).toLocaleDateString(); } catch { return label; }
                  }}
                />
                <Legend />
                <Area yAxisId="right" type="monotone" dataKey="drawdown" fill="#fecaca" stroke="var(--danger)" name="Drawdown ($)" />
                <Line yAxisId="left" type="monotone" dataKey="equity" stroke="var(--primary)" strokeWidth={2} dot={false} name="Equity Curve ($)" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : drawdownAnalysis && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1rem", color: "var(--text-primary)" }}>
            📉 Equity & Drawdown
          </h2>
          <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
            No closed trade data available for the selected period.
          </div>
        </div>
      )}

      {/* Win/Loss Comparison */}
      {winLossComparison && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            📊 Winning vs Losing Trades
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1.5rem" }}>
            <div style={{ padding: "1rem", backgroundColor: "var(--success-bg)", borderRadius: "0.5rem", border: "1px solid #10b981" }}>
              <div style={{ fontSize: "0.875rem", color: "var(--success)", marginBottom: "0.5rem" }}>Winners</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--success)" }}>{winLossComparison.winners.count}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                Avg P&L: ${safeNumber(winLossComparison.winners.avgPnl, 0).toFixed(2)}<br/>
                Avg Duration: {(winLossComparison.winners.avgDuration / 60).toFixed(1)}h
              </div>
            </div>
            <div style={{ padding: "1rem", backgroundColor: "var(--danger-bg)", borderRadius: "0.5rem", border: "1px solid #ef4444" }}>
              <div style={{ fontSize: "0.875rem", color: "var(--danger)", marginBottom: "0.5rem" }}>Losers</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--danger)" }}>{winLossComparison.losers.count}</div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                Avg P&L: ${safeNumber(winLossComparison.losers.avgPnl, 0).toFixed(2)}<br/>
                Avg Duration: {(winLossComparison.losers.avgDuration / 60).toFixed(1)}h
              </div>
            </div>
            <div style={{ padding: "1rem", backgroundColor: "var(--warning-bg)", borderRadius: "0.5rem", border: "1px solid #eab308" }}>
              <div style={{ fontSize: "0.875rem", color: "var(--warning)", marginBottom: "0.5rem" }}>Win/Loss Ratio</div>
              <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--warning)" }}>
                {safeNumber(winLossComparison.insights.avgWinLossRatio, 0).toFixed(2)}:1
              </div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                {winLossComparison.insights.winnersTendToBeHeldLonger ?
                  "Winners held longer ✓" : "Winners held shorter"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Performance */}
      {strategyAnalysis && strategyAnalysis.strategies.length > 0 && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            🎲 Strategy Performance
          </h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "0.75rem", textAlign: "left", fontWeight: "600", color: "var(--text-primary)" }}>Strategy</th>
                  <th style={{ padding: "0.75rem", textAlign: "right", fontWeight: "600", color: "var(--text-primary)" }}>Trades</th>
                  <th style={{ padding: "0.75rem", textAlign: "right", fontWeight: "600", color: "var(--text-primary)" }}>Win Rate</th>
                  <th style={{ padding: "0.75rem", textAlign: "right", fontWeight: "600", color: "var(--text-primary)" }}>Total P&L</th>
                  <th style={{ padding: "0.75rem", textAlign: "right", fontWeight: "600", color: "var(--text-primary)" }}>Avg P&L</th>
                  <th style={{ padding: "0.75rem", textAlign: "right", fontWeight: "600", color: "var(--text-primary)" }}>Profit Factor</th>
                </tr>
              </thead>
              <tbody>
                {strategyAnalysis.strategies.map((strategy, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "0.75rem", fontWeight: "500" }}>
                      {strategy.name}
                      {strategy === strategyAnalysis.bestStrategy && (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "var(--success)" }}>🏆 Best</span>
                      )}
                    </td>
                    <td style={{ padding: "0.75rem", textAlign: "right" }}>{strategy.totalTrades}</td>
                    <td style={{ padding: "0.75rem", textAlign: "right" }}>
                      <span style={{ color: strategy.winRate >= 50 ? "var(--success)" : "var(--danger)" }}>
                        {strategy.winRate}%
                      </span>
                    </td>
                    <td style={{ padding: "0.75rem", textAlign: "right", fontWeight: "500", color: strategy.totalPnl >= 0 ? "var(--success)" : "var(--danger)" }}>
                      ${strategy.totalPnl.toFixed(2)}
                    </td>
                    <td style={{ padding: "0.75rem", textAlign: "right", color: strategy.avgPnl >= 0 ? "var(--success)" : "var(--danger)" }}>
                      ${strategy.avgPnl.toFixed(2)}
                    </td>
                    <td style={{ padding: "0.75rem", textAlign: "right" }}>
                      <span style={{ color: strategy.profitFactor >= 1.5 ? "var(--success)" : strategy.profitFactor >= 1.0 ? "var(--warning)" : "var(--danger)" }}>
                        {strategy.profitFactor.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Setup Analysis */}
      {setupAnalysis && setupAnalysis.setups.length > 0 && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            🎯 Setup Performance
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
            {setupAnalysis.setups.map((setup, idx) => (
              <div
                key={idx}
                style={{
                  padding: "1rem",
                  border: setup === setupAnalysis.bestSetup ? "2px solid #10b981" : "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  backgroundColor: setup === setupAnalysis.bestSetup ? "var(--success-bg)" : "var(--bg-secondary)"
                }}
              >
                <div style={{ fontSize: "0.875rem", fontWeight: "600", marginBottom: "0.5rem" }}>
                  {setup.name}
                  {setup === setupAnalysis.bestSetup && <span style={{ marginLeft: "0.5rem" }}>🏆</span>}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  {setup.count} trades | {setup.winRate}% win rate
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: "bold", marginTop: "0.5rem", color: setup.totalPnl >= 0 ? "var(--success)" : "var(--danger)" }}>
                  ${setup.totalPnl.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Patterns */}
      {timePatterns && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            ⏰ Time-Based Patterns
          </h2>

          {/* Best Times Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
            {timePatterns.bestHour && (
              <div style={{ padding: "1rem", backgroundColor: "var(--success-bg)", borderRadius: "0.5rem" }}>
                <div style={{ fontSize: "0.875rem", color: "var(--success)", marginBottom: "0.5rem" }}>Best Trading Hour</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--success)" }}>
                  {timePatterns.bestHour.hour}:00
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                  {timePatterns.bestHour.count} trades | ${timePatterns.bestHour.avgPnl.toFixed(2)} avg
                </div>
              </div>
            )}
            {timePatterns.bestDay && (
              <div style={{ padding: "1rem", backgroundColor: "var(--primary-light)", borderRadius: "0.5rem" }}>
                <div style={{ fontSize: "0.875rem", color: "var(--primary)", marginBottom: "0.5rem" }}>Best Trading Day</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--primary)" }}>
                  {timePatterns.bestDay.day}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                  {timePatterns.bestDay.count} trades | ${timePatterns.bestDay.avgPnl.toFixed(2)} avg
                </div>
              </div>
            )}
          </div>

          {/* Hourly Chart */}
          {hourlyChartData.length > 0 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h3 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "1rem" }}>Performance by Hour</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hourlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === "avgPnl" ? `$${value.toFixed(2)}` : value,
                    name === "avgPnl" ? "Avg P&L" : name === "winRate" ? "Win Rate %" : "Trades"
                  ]} />
                  <Bar dataKey="avgPnl" fill="var(--primary)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Daily Chart */}
          {dailyChartData.length > 0 && (
            <div>
              <h3 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "1rem" }}>Performance by Day of Week</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === "avgPnl" ? `$${value.toFixed(2)}` : value,
                    name === "avgPnl" ? "Avg P&L" : name === "winRate" ? "Win Rate %" : "Trades"
                  ]} />
                  <Bar dataKey="avgPnl" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Trade Duration Analysis */}
      {durationAnalysis && durationAnalysis.durations.some(d => d.count > 0) && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            ⏱️ Trade Duration Analysis
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
            {durationAnalysis.durations.filter(d => d.count > 0).map((duration, idx) => (
              <div
                key={idx}
                style={{
                  padding: "1rem",
                  border: duration === durationAnalysis.bestDuration ? "2px solid #10b981" : "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  backgroundColor: duration === durationAnalysis.bestDuration ? "var(--success-bg)" : "var(--bg-secondary)"
                }}
              >
                <div style={{ fontSize: "0.875rem", fontWeight: "600", marginBottom: "0.5rem" }}>
                  {duration.label}
                  {duration === durationAnalysis.bestDuration && <span style={{ marginLeft: "0.5rem" }}>🏆</span>}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  {duration.count} trades | {duration.winRate}% win rate
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: "bold", marginTop: "0.5rem", color: duration.avgPnl >= 0 ? "var(--success)" : "var(--danger)" }}>
                  ${duration.avgPnl.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk/Reward Analysis */}
      {riskRewardAnalysis && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            ⚖️ Risk/Reward Analysis
          </h2>
          <div style={{ padding: "1rem", backgroundColor: "var(--bg-tertiary)", borderRadius: "0.5rem", marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Average Risk/Reward Ratio</div>
            <div style={{ fontSize: "2rem", fontWeight: "bold", color: "var(--text-primary)" }}>
              {riskRewardAnalysis.averageRiskReward.toFixed(2)}:1
            </div>
            <div style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.5rem", fontStyle: "italic" }}>
              {riskRewardAnalysis.insight}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "1rem" }}>
            {Object.entries(riskRewardAnalysis.buckets).map(([key, bucket]) => (
              <div key={key} style={{ padding: "0.75rem", border: "1px solid #e5e7eb", borderRadius: "0.5rem" }}>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>{bucket.label}</div>
                <div style={{ fontSize: "0.875rem", fontWeight: "600" }}>{bucket.count} trades</div>
                <div style={{ fontSize: "0.875rem", color: bucket.avgPnl >= 0 ? "var(--success)" : "var(--danger)" }}>
                  ${bucket.avgPnl.toFixed(2)} avg
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mistake Analysis */}
      {mistakeAnalysis && mistakeAnalysis.mistakes.length > 0 && (
        <div style={{ ...styles.card, marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
            ⚠️ Common Mistakes
          </h2>
          <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "var(--danger-bg)", borderRadius: "0.5rem" }}>
            <div style={{ fontSize: "0.875rem", color: "var(--danger)" }}>
              Total Mistakes: {mistakeAnalysis.totalMistakes} |
              Total Cost: ${mistakeAnalysis.mistakes.reduce((sum, m) => sum + m.totalPnl, 0).toFixed(2)}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {mistakeAnalysis.mistakes.map((mistake, idx) => (
              <div
                key={idx}
                style={{
                  padding: "1rem",
                  border: "1px solid #fee2e2",
                  borderRadius: "0.5rem",
                  backgroundColor: "var(--bg-secondary)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div>
                  <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{mistake.category}</div>
                  <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                    {mistake.count} occurrences | Avg impact: ${mistake.avgPnl.toFixed(2)}
                  </div>
                </div>
                <div style={{ fontSize: "1.25rem", fontWeight: "bold", color: "var(--danger)" }}>
                  ${mistake.totalPnl.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* AI Edge Report */}
      <div className="mt-6">
        <EdgeReport />
      </div>

      {/* Time-of-Day Heatmap */}
      <div className="mt-6">
        <TimeHeatmap period={period} appliedCustomDates={appliedCustomDates} />
      </div>
    </div>
  );
};

export default Analytics;
